import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { httpsCallable } from "firebase/functions";
import { CheckCircle, XCircle, Loader2, Mail } from "lucide-react";
import { functions } from "../config/firebase";

const VerifyEmail: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<"loading" | "success" | "error">(
    "loading",
  );
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    const verifyEmail = async () => {
      const token = searchParams.get("token");

      if (!token) {
        setStatus("error");
        setErrorMessage("Token di verifica mancante.");
        return;
      }

      try {
        // Call backend function to verify email
        console.log("Verifying email with token:", token);
        const verifyEmailWithToken = httpsCallable(
          functions,
          "verifyEmailWithToken",
        );
        const result = await verifyEmailWithToken({ token });
        console.log("Email verification result:", result.data);
        setStatus("success");
      } catch (error) {
        console.error("Error verifying email:", error);
        setStatus("error");

        if (error instanceof Error) {
          console.error("Full error details:", error.message);

          if (
            error.message.includes("not-found") ||
            error.message.includes("non valido")
          ) {
            setErrorMessage(
              "Il link di verifica non è valido. Richiedi un nuovo link.",
            );
          } else if (
            error.message.includes("already-exists") ||
            error.message.includes("già stato utilizzato")
          ) {
            setErrorMessage(
              "Questo link di verifica è già stato utilizzato. Effettua il login.",
            );
          } else if (
            error.message.includes("deadline-exceeded") ||
            error.message.includes("scaduto")
          ) {
            setErrorMessage(
              "Il link di verifica è scaduto. Richiedi un nuovo link.",
            );
          } else {
            setErrorMessage(
              "Errore durante la verifica dell'email. Riprova più tardi.",
            );
          }
        } else {
          setErrorMessage("Errore sconosciuto. Riprova più tardi.");
        }
      }
    };

    verifyEmail();
  }, [searchParams]);

  return (
    <div className="min-h-screen pt-24 pb-12 bg-gray-50 flex items-center">
      <div className="container-custom">
        <div className="max-w-md mx-auto">
          <div className="card p-8 text-center">
            {status === "loading" && (
              <>
                <div className="inline-flex items-center justify-center w-20 h-20 bg-blue-100 rounded-full mb-6">
                  <Loader2 className="w-10 h-10 text-romanian-blue animate-spin" />
                </div>
                <h1 className="font-display text-2xl font-bold text-gray-900 mb-4">
                  Verifica in corso...
                </h1>
                <p className="text-gray-600">
                  Stiamo verificando il tuo indirizzo email.
                </p>
              </>
            )}

            {status === "success" && (
              <>
                <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-6">
                  <CheckCircle className="w-10 h-10 text-green-600" />
                </div>
                <h1 className="font-display text-2xl font-bold text-gray-900 mb-4">
                  Email Verificata!
                </h1>
                <p className="text-gray-600 mb-6">
                  Il tuo indirizzo email è stato verificato con successo. Ora
                  puoi accedere al tuo account.
                </p>
                <Link
                  to="/login?verified=true"
                  className="btn-primary inline-flex items-center space-x-2"
                >
                  <span>Vai al Login</span>
                </Link>
              </>
            )}

            {status === "error" && (
              <>
                <div className="inline-flex items-center justify-center w-20 h-20 bg-red-100 rounded-full mb-6">
                  <XCircle className="w-10 h-10 text-red-600" />
                </div>
                <h1 className="font-display text-2xl font-bold text-gray-900 mb-4">
                  Verifica Fallita
                </h1>
                <p className="text-gray-600 mb-6">{errorMessage}</p>
                <div className="space-y-3">
                  <Link
                    to="/login"
                    className="btn-primary inline-flex items-center space-x-2 w-full justify-center"
                  >
                    <Mail className="w-5 h-5" />
                    <span>Vai al Login</span>
                  </Link>
                  <Link
                    to="/register"
                    className="btn-secondary inline-flex items-center space-x-2 w-full justify-center"
                  >
                    <span>Registrati di nuovo</span>
                  </Link>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerifyEmail;
